<template>
  <div class="form">
    <form @submit.prevent @keydown.enter="$event.preventDefault()">
      <CurrencyInput
        key="sellCoin"
        name="coin"
        type="text"
        label="Sell"
        placeholder="Coin amount"
        :defaultValue="{
          coinType: sellForm.coinType,
        }"
        :items="coinInputs"
        :isLoading="isFetchingData.coinType"
        :errors="sellOrderFormStore.getErrorByField('coinAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />
      <CurrencyInput
        key="sellFiat"
        name="fiat"
        type="text"
        label="Receive"
        placeholder="Fiat amount"
        :defaultValue="{
          fiatType: sellForm.fiatType,
        }"
        :items="fiatInputs"
        :isLoading="isFetchingData.fiatType"
        :errors="sellOrderFormStore.getErrorByField('fiatAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />

      <FormSelect
        type="text"
        name="blockchain"
        label="Selected blockchain"
        :key="'blockchain'"
        :disabled="disabledBlockchainSelect"
        :items="blockchainsByCoin"
        :defaultItem="defaultBlockchain"
        :isLoading="isFetchingData.blockchain"
        @dropdownValueUpdate="updateDropdown"
      />
      <ButtonBase
        :isDisabled="
          sellOrderFormStore.formHasErrors ||
          globalStore.getIsFormLoading ||
          sellOrderFormStore.feeData.isLoading
        "
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Create Order
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { whenever } from "@vueuse/core";

import { useGlobalStore } from "@/stores/globalStore";
import { settings } from "@/stores/forms/orderFormStore";
import { useSellOrderFormStore } from "@/stores/forms/sellOrderFormStore";

import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";
import useDynamicPricingCalculator from "@/composables/helpers/useDynamicPricingCalculator";

import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import CurrencyInput from "@/components/ui/inputs/CurrencyInput.vue";

const router = useRouter();
const sellOrderFormStore = useSellOrderFormStore();
const globalStore = useGlobalStore();
const { watchFormAndUpdateStore } = useWatchAndUpdate();
const { dynamicPricingCalculator } = useDynamicPricingCalculator();

const { getFiats, getCoins, initOrder } = useOrderEndpoints();

const fiatInputs = ref([]);
const coinInputs = ref([]);
const blockchainsByCoin = ref([]);
const defaultBlockchain = ref("");
const sellForm = ref({
  fiatType: "",
});
const isFetchingData = ref({
  fiatAmount: true,
  coinAmount: true,
});

// to set the validation check on the store for widget
const handleFormStoreKeyRequiredFieldForSubmitButton = (isRequiredFlag) => {
  const keysToSetRequiredFieldFor = ["payment"];
  keysToSetRequiredFieldFor.forEach((key) =>
    sellOrderFormStore.setRequiredFieldByKey(key, isRequiredFlag)
  );
};

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  if (value) sellForm.value[field] = value;
  dynamicPricingCalculator(field, value, sellForm, sellOrderFormStore);
};

const updateCurrencyInput = (inputData) => {
  const field = Object.keys(inputData)[0];
  const value = Object.values(inputData)[0];
  const lockFiat = field === "fiatAmount";

  sellForm.value[field] = value;
  sellForm.value.lockFiat = lockFiat;

  dynamicPricingCalculator(field, value, sellForm, sellOrderFormStore);
};

const changeLoadingValueByName = (name, isLoading) => {
  isFetchingData.value[name] = isLoading;
};

const setDefaultsByLocalStorage = () => {
  Object.keys(sellOrderFormStore.form).forEach((key) => {
    if (key === "fiatType") {
      const defaultFiatType =
        fiatInputs.value.indexOf(settings.defaultSellFiatType) >= 0
          ? settings.defaultSellFiatType
          : fiatInputs.value[0];
      sellForm.value.fiatType = defaultFiatType;
    } else if (key === "coinType") {
      const defaultCoinType =
        fiatInputs.value.indexOf(settings.defaultCoinType) >= 0
          ? settings.defaultCoinType
          : coinInputs.value[0];
      sellForm.value.coinType = defaultCoinType;
    } else {
      sellForm.value[key] = sellOrderFormStore.form[key].value;
    }
  });

  const lockFiat = sellOrderFormStore.form.lockFiat.value;
  const fieldToTriggerCalcBy = lockFiat ? "fiatAmount" : "coinAmount";
  dynamicPricingCalculator(
    fieldToTriggerCalcBy,
    sellForm.value[fieldToTriggerCalcBy],
    sellForm,
    sellOrderFormStore
  );
};

const runOnMounted = async () => {
  const fiatResponse = await getFiats("sell");
  const coinResponse = await getCoins("sell");
  await globalStore.fetchPayments();

  fiatInputs.value = fiatResponse.data.data.fiats.map((el) => {
    return el.fiat_code;
  });
  coinInputs.value = coinResponse.data.data.coins.map((el) => el.coin_code);

  handleFormStoreKeyRequiredFieldForSubmitButton(false);
  sellOrderFormStore.setCoinData(coinResponse.data.data.coins);
  setDefaultsByLocalStorage();
  changeLoadingValueByName("fiatType", false);
  changeLoadingValueByName("coinType", false);
};
const disabledBlockchainSelect = computed(() => {
  if (
    blockchainsByCoin.value &&
    blockchainsByCoin.value.includes("No valid blockchains available")
  ) {
    return true;
  }

  return false;
});

const findBlockchainsByCoin = () => {
  // if the selected coin also has the selected blockchain, don't update the value
  if (
    sellOrderFormStore.findAllBlockchainCodesByCoins.includes(
      sellOrderFormStore.form.blockchain.value
    )
  ) {
    defaultBlockchain.value = sellOrderFormStore.form.blockchain.value;
  } else {
    defaultBlockchain.value = "";
  }
  blockchainsByCoin.value = sellOrderFormStore.findAllBlockchainCodesByCoins;
};

watch(
  () => globalStore.$state.token,
  (val) => {
    if (val) {
      runOnMounted();
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => sellForm.value.coinType,
  () => {
    sellOrderFormStore.setCoinType(sellForm.value.coinType);
    findBlockchainsByCoin();
    updateDropdown({ blockchain: blockchainsByCoin.value[0] });
  },
  { deep: true }
);

whenever(
  () => fiatInputs.value.length > 0 && coinInputs.value.length > 0,
  () => {
    setDefaultsByLocalStorage();
  },
  { deep: true }
);

const handleFormSuccess = () => {
  router.push({
    path: "/",
    query: { ...sellOrderFormStore.getFormPayload, orderMode: "SELL" },
  });
};

const handleFormSubmit = async () => {
  globalStore.setIsFormLoading(true);
  handleFormSuccess();
  handleFormStoreKeyRequiredFieldForSubmitButton(true);
};
watchFormAndUpdateStore(sellForm, sellOrderFormStore, () => {});
</script>
<style scoped></style>
